// Variants: components grouped in a component set and named "Prop=Value, Prop2=Value".
import type { Document } from './document.ts';
import type { Editor } from './editor.ts';
import type { NodeData, NodeId, Op } from './types.ts';
import { instantiate } from './components.ts';
import { parseColor } from './util.ts';

export function parseVariantName(name: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of name.split(',')) {
    const [k, ...rest] = part.split('=');
    if (!rest.length) continue;
    out[k.trim()] = rest.join('=').trim();
  }
  return out;
}

export function formatVariantName(props: Record<string, string>): string {
  return Object.entries(props)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
}

export function componentSetOf(doc: Document, componentId: NodeId): NodeData | undefined {
  const p = doc.get(doc.get(componentId)?.parent);
  return p?.isComponentSet ? p : undefined;
}

/** All variant properties of a set and their possible values. */
export function variantOptions(doc: Document, setId: NodeId): Record<string, string[]> {
  const opts: Record<string, string[]> = {};
  for (const c of doc.children(setId)) {
    if (c.type !== 'COMPONENT') continue;
    for (const [k, v] of Object.entries(parseVariantName(c.name))) {
      opts[k] ??= [];
      if (!opts[k].includes(v)) opts[k].push(v);
    }
  }
  return opts;
}

/** The component in a set that best matches the requested properties. */
export function findVariant(doc: Document, setId: NodeId, want: Record<string, string>): NodeData | undefined {
  let best: NodeData | undefined;
  let bestScore = -1;
  for (const c of doc.children(setId)) {
    if (c.type !== 'COMPONENT') continue;
    const props = parseVariantName(c.name);
    let score = 0;
    for (const [k, v] of Object.entries(want)) if (props[k]?.toLowerCase() === v.toLowerCase()) score++;
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  return best;
}

/** Wrap components into a component set (auto-layout grid with the familiar dashed outline). */
export function combineAsVariants(editor: Editor, ids: NodeId[], name?: string): NodeId {
  const comps = editor.topLevel(ids).filter((n) => n.type === 'COMPONENT');
  if (comps.length < 1) throw new Error('Select components to combine');
  // ensure every component has a variant-style name
  editor.transact('Combine as variants', () => {
    comps.forEach((c, i) => {
      if (!c.name.includes('=')) editor.update(c.id, { name: `Property 1=${i === 0 ? 'Default' : `Variant${i + 1}`}` }, 'Name variant');
    });
  });
  const setId = editor.wrap(
    comps.map((c) => c.id),
    'FRAME',
    {
      name: name ?? 'Component set',
      isComponentSet: true,
      fills: [],
      strokes: [{ type: 'SOLID', color: parseColor('#9747ff') }],
      strokeDash: [6, 4],
      strokeWeight: 1,
      cornerRadius: 6,
      layoutMode: 'HORIZONTAL',
      itemSpacing: 20,
      counterAxisSpacing: 20,
      paddingTop: 20,
      paddingRight: 20,
      paddingBottom: 20,
      paddingLeft: 20,
      layoutSizingH: 'HUG',
      layoutSizingV: 'HUG',
      clipsContent: false,
    },
  );
  return setId;
}

/** Point an instance at another component, keeping text overrides on layers with the same name. */
export function swapInstance(editor: Editor, instanceId: NodeId, componentId: NodeId) {
  const doc = editor.doc;
  const inst = doc.must(instanceId);
  if (inst.type !== 'INSTANCE') throw new Error('Not an instance');
  const keepText = new Map<string, string>();
  doc.walk(instanceId, (n) => {
    if (n.id !== instanceId && n.type === 'TEXT' && n.overrides?.includes('characters')) keepText.set(n.name, n.characters ?? '');
  });
  // interactions added on the instance itself (beyond its main component's) carry over to the new variant
  const mainReactions = new Set((doc.get(inst.componentId!)?.reactions ?? []).map((r) => JSON.stringify(r)));
  const ownReactions = inst.overrides?.includes('reactions') ? (inst.reactions ?? []).filter((r) => !mainReactions.has(JSON.stringify(r))) : [];
  editor.transact('Swap variant', () => {
    const ops: Op[] = doc.children(instanceId).map((c) => ({ op: 'delete', id: c.id }));
    editor.applyOps(ops);
    const { ops: created } = instantiate(doc, componentId, inst.parent!, inst.index);
    const root = created[0] as Extract<Op, { op: 'create' }>;
    const rootNode = root.node;
    // reuse the existing instance node: re-point it, copy the component's look, re-create children under it
    const look: Record<string, unknown> = { ...rootNode };
    for (const k of ['id', 'parent', 'index', 'x', 'y', 'name', 'rotation', 'locked', 'visible', 'layoutPositioning', 'layoutSizingH', 'layoutSizingV', 'flipX', 'flipY', 'variableModes']) delete look[k];
    const keep = ownReactions.length ? { reactions: [...((look.reactions as NodeData['reactions']) ?? []), ...ownReactions], overrides: ['reactions'] } : { overrides: [] };
    editor.applyOps([{ op: 'set', id: instanceId, props: { ...(look as unknown as NodeData), componentId, ...keep } }]);
    const kids = created.slice(1).map((op) => {
      const c = op as Extract<Op, { op: 'create' }>;
      const node = { ...c.node, parent: c.node.parent === rootNode.id ? instanceId : c.node.parent };
      if (node.type === 'TEXT' && keepText.has(node.name)) {
        node.characters = keepText.get(node.name);
        node.overrides = ['characters'];
      }
      return { op: 'create', node } as Op;
    });
    editor.applyOps(kids);
  });
}

/** Change one variant property of an instance (e.g. State → Hover). */
export function setVariant(editor: Editor, instanceId: NodeId, props: Record<string, string>) {
  const doc = editor.doc;
  const inst = doc.must(instanceId);
  if (!inst.componentId) throw new Error('Not an instance');
  const set = componentSetOf(doc, inst.componentId);
  if (!set) throw new Error('This instance is not part of a component set');
  const current = parseVariantName(doc.must(inst.componentId).name);
  const target = findVariant(doc, set.id, { ...current, ...props });
  if (target && target.id !== inst.componentId) swapInstance(editor, instanceId, target.id);
}
